/**
 * FmOperationalWorkspaceRoot — descriptor-only non-linear decision workspace.
 * Renderer owns stages, status, region layout, mutation receipts, dialogs,
 * responsive order, and governed map/media placement. It never fetches.
 */
/* eslint-disable react-refresh/only-export-components -- canonical entrypoint also exports its descriptor contract and validator. */
import React from 'react';
import { FmErrorBoundary } from './FmErrorBoundary';
import { FmSpinner } from './FmFeedback';
import type { OperationalActionActivate } from './FmOperationalWorkspaceStateSurfaces';
import { actionClick, blockedStageId } from './FmOperationalWorkspaceStatus';
import { OperationalWorkspaceFrame } from './internal/OperationalWorkspaceFrame';
import type {
  FmOperationalDialogDescriptor,
  FmOperationalWorkspaceDescriptor,
  FmOperationalWorkspaceRootProps,
} from './FmOperationalWorkspaceRoot.types';
import {
  assertFmOperationalRenderFailure,
  assertFmOperationalWorkspaceShell,
} from './FmOperationalWorkspaceRoot.validation';
import './FmOperationalWorkspaceRoot.css';

export type {
  FmOperationalActionDescriptor,
  FmOperationalBannerDescriptor,
  FmOperationalBlockerDescriptor,
  FmOperationalEmptyStateDescriptor,
  FmOperationalErrorStateDescriptor,
  FmOperationalReadyWorkspaceDescriptor,
  FmOperationalRegionScope,
  FmOperationalStaleStateDescriptor,
  FmOperationalStateScopeDescriptor,
  FmOperationalDialogDescriptor,
  FmOperationalFilterDescriptor,
  FmOperationalFormFlowCapabilityDescriptor,
  FmOperationalChoiceSummary,
  FmOperationalChoiceSummaryItem,
  FmOperationalChoiceSummaryLead,
  FmOperationalCompactChoiceSummary,
  FmOperationalCompactGuidanceDescriptor,
  FmOperationalGuidanceChrome,
  FmOperationalGuidanceDescriptor,
  FmOperationalStandardGuidanceDescriptor,
  FmOperationalMutationDescriptor,
  FmOperationalReceiptDescriptor,
  FmOperationalRegionDescriptor,
  FmOperationalRevisionDescriptor,
  FmOperationalSelectionDescriptor,
  FmOperationalStageDescriptor,
  FmOperationalWorkspaceVocabularyDescriptor,
  FmOperationalWorkspaceDescriptor,
  FmOperationalWorkspaceEvent,
  FmOperationalWorkspaceMastheadDescriptor,
  FmOperationalReturnActionDescriptor,
  FmOperationalRenderFailureDescriptor,
  FmOperationalWorkspaceRootProps,
  FmOperationalWorkspaceState,
} from './FmOperationalWorkspaceRoot.types';
export { assertFmOperationalWorkspaceDescriptor } from './FmOperationalWorkspaceRoot.validation';
export { operationalWorkspaceStatus } from './FmOperationalWorkspaceStatus';
export { OperationalWorkspaceDialogSurface } from './internal/OperationalWorkspaceDialogs';
export type {
  FmOperationalStatusDescriptor,
  FmOperationalStatusKey,
} from './FmOperationalWorkspaceStatus';

/**
 * Why the ordinary stage toolbar is inhibited, if it is. A blocker inhibits
 * only the stage that owns its scope, so a scoped blocker leaves the rest of
 * the workspace usable (gap G1); a workspace-scoped blocker inhibits everything
 * except its own resolving action.
 */
function actionInhibitionReason(
  descriptor: FmOperationalWorkspaceDescriptor,
  stageId: string,
): string | null {
  /* Optimistic concurrency first, and unconditionally. A scoped blocker used to
     return early here, which suppressed the revision-conflict and pending-
     mutation guards on every stage the blocker did not own and re-enabled
     mixed-revision confirmation. */
  if (descriptor.revision.status !== 'current') return descriptor.revision.label;
  if (descriptor.mutation.status === 'pending' || descriptor.mutation.status === 'conflict') {
    return descriptor.mutation.label;
  }
  const blockerReceipt = descriptor.mutation.receipts.find((receipt) => receipt.kind === 'blocker');
  if (blockerReceipt) return blockerReceipt.message;
  if (descriptor.state === 'blocked') {
    /* A scoped blocker inhibits only the stage that owns its scope, so the rest
       of the workspace stays usable (gap G1). */
    const scopeStageId = blockedStageId(descriptor, descriptor.blocked);
    return scopeStageId === null || scopeStageId === stageId ? descriptor.blocked.name : null;
  }
  if (descriptor.state !== 'ready') return descriptor.stateLabel;
  return null;
}

function scrollStageStartBelowDock(stageContent: HTMLElement, compact: boolean): void {
  const workspace = stageContent.parentElement;
  /* The compact chrome has no header past the first step and only a one-line
     one on it, so every step opens at the top of the scroller: the dock is
     already the first thing there. */
  if (workspace && compact) {
    workspace.scrollTo({ top: 0, behavior: 'auto' });
    return;
  }
  const dock = workspace?.querySelector<HTMLElement>(
    ':scope > .fm-operational-workspace__chrome-dock',
  );
  if (!workspace || !dock) return;
  const workspaceRect = workspace.getBoundingClientRect();
  const stageRect = stageContent.getBoundingClientRect();
  /* Where the dock's bottom edge sits once it is stuck, not where it is now:
     from the top of the page the dock still rides under the masthead, so its
     current edge left the masthead in view and every step reopened on the
     page header instead of on its own work. */
  const stuckTop = Number.parseFloat(window.getComputedStyle(dock).top);
  if (!Number.isFinite(stuckTop)) return;
  const dockBottomInset = stuckTop + dock.offsetHeight;
  workspace.scrollTo({
    top: Math.max(0, workspace.scrollTop + stageRect.top - workspaceRect.top - dockBottomInset),
    behavior: 'auto',
  });
}

/** @internal Render a descriptor already validated by an owning archetype. */
export const OperationalWorkspaceSurface: React.FC<FmOperationalWorkspaceRootProps> = ({
  descriptor,
  presentation = 'page',
  'data-testid': dataTestId = 'fm-operational-workspace',
}) => {
  /* The descriptor's stage is the ONLY stage this renders.
     It used to be mirrored into local state that a tab click set OPTIMISTICALLY,
     while the reconciling effect below fired only when `descriptor.activeStageId`
     changed away from the last value it had recorded. So when the owner clicked a
     stage and the page never published that stage — a stage publication dropped as
     stale because the browser had already moved on, which is the correct thing for
     the route to do — the descriptor stayed on the stage it was already on, the
     effect's dependency never changed, and the local copy kept rendering a stage
     nothing had asked for. FOREVER: nothing could contradict it, because the
     contradiction it waited for was a CHANGE in the descriptor rather than a
     DISAGREEMENT with it. The workspace then reported `data-state=ready` while
     serving the Päätös stage under a `?stage=plan` URL, so the whole stands ledger
     was absent and every consumer read that as "this plan has no stands".
     A descriptor-driven stage cannot diverge, and `descriptor.stages`
     already carries its own `status: 'current'` derived from this same field, so
     the mirror also made the rail's selected tab and its current-stage chip
     disagree for the whole optimistic window. */
  const activeStageId = descriptor.activeStageId;
  const [openDialogId, setOpenDialogId] = React.useState<string | null>(null);
  const openDialogSnapshotRef = React.useRef<FmOperationalDialogDescriptor | null>(null);
  const dialogTriggerRef = React.useRef<HTMLButtonElement | null>(null);
  const renderedActiveStageIdRef = React.useRef(descriptor.activeStageId);
  const renderedRevisionIdRef = React.useRef(descriptor.revision.id);
  const stageContentRef = React.useRef<HTMLElement | null>(null);
  const stageFocusRequestedRef = React.useRef(false);
  const tabActivatedStageRef = React.useRef<string | null>(null);
  const stageScrollRequestRef = React.useRef<string | null>(null);
  const compactChrome = descriptor.guidance?.chrome === 'compact';
  const stagesTestId = `${dataTestId}-stages`;
  const bodyTestId = `${dataTestId}-body`;
  /* Focus bookkeeping only. A stage the owner reached by clicking its own tab
     keeps focus on that tab; every other arrival (a route move, a body link, a
     restored history entry) moves focus into the stage content. */
  React.useEffect(() => {
    if (renderedActiveStageIdRef.current === descriptor.activeStageId) return;
    renderedActiveStageIdRef.current = descriptor.activeStageId;
    const originatedFromTab = tabActivatedStageRef.current === descriptor.activeStageId;
    tabActivatedStageRef.current = null;
    stageFocusRequestedRef.current = !originatedFromTab;
  }, [descriptor.activeStageId]);
  React.useLayoutEffect(() => {
    const requested = stageScrollRequestRef.current === activeStageId;
    stageScrollRequestRef.current = null;
    if (requested && presentation === 'page' && stageContentRef.current) {
      scrollStageStartBelowDock(stageContentRef.current, compactChrome);
    }
  }, [activeStageId, presentation, compactChrome]);
  React.useEffect(() => {
    if (!stageFocusRequestedRef.current) return;
    stageFocusRequestedRef.current = false;
    if (stageContentRef.current) {
      stageContentRef.current.focus({ preventScroll: true });
    }
  }, [activeStageId]);
  const currentDialogTrigger = React.useCallback(() => {
    const previous = dialogTriggerRef.current;
    if (previous?.isConnected) return previous;
    const testId = previous?.getAttribute('data-testid');
    if (!testId) return null;
    const workspace = Array.from(
      document.querySelectorAll<HTMLElement>('.fm-operational-workspace'),
    ).find((candidate) => candidate.getAttribute('data-testid') === dataTestId);
    return (
      Array.from(workspace?.querySelectorAll<HTMLButtonElement>('button[data-testid]') ?? []).find(
        (candidate) => candidate.getAttribute('data-testid') === testId,
      ) ?? null
    );
  }, [dataTestId]);
  const restoreDialogTrigger = React.useCallback(() => {
    window.requestAnimationFrame(() => {
      currentDialogTrigger()?.focus();
      window.requestAnimationFrame(() => {
        currentDialogTrigger()?.focus();
      });
    });
  }, [currentDialogTrigger]);
  const dialogOpen = openDialogId !== null || descriptor.formFlow?.open === true;
  const dialogWasOpenRef = React.useRef(dialogOpen);
  React.useEffect(() => {
    const wasOpen = dialogWasOpenRef.current;
    dialogWasOpenRef.current = dialogOpen;
    if (wasOpen && !dialogOpen) currentDialogTrigger()?.focus();
  }, [currentDialogTrigger, dialogOpen]);
  const setOpenDialog = React.useCallback(
    (id: string | null) => {
      openDialogSnapshotRef.current = id
        ? (descriptor.dialogs.find((dialog) => dialog.id === id) ?? null)
        : null;
      setOpenDialogId(id);
    },
    [descriptor.dialogs],
  );
  React.useEffect(() => {
    if (renderedRevisionIdRef.current === descriptor.revision.id) return;
    const shouldRestoreDialogFocus = openDialogId !== null;
    renderedRevisionIdRef.current = descriptor.revision.id;
    setOpenDialog(null);
    if (shouldRestoreDialogFocus) restoreDialogTrigger();
  }, [descriptor.revision.id, openDialogId, restoreDialogTrigger, setOpenDialog]);
  const activeStage =
    descriptor.stages.find((stage) => stage.id === activeStageId) ?? descriptor.stages[0];

  const inhibitionReason = actionInhibitionReason(descriptor, activeStage.id);
  const inhibited = inhibitionReason !== null;
  /* A state surface's own control resolves the state that produced it, so it is
     never inhibited by that state — the validator requires it to declare
     `availableWhenUnavailable`. */
  const onActivate = React.useCallback<OperationalActionActivate>(
    (action, trigger) => {
      dialogTriggerRef.current = trigger;
      actionClick(action, descriptor, setOpenDialog, false);
    },
    [descriptor, setOpenDialog],
  );

  return (
    <OperationalWorkspaceFrame
      descriptor={descriptor}
      presentation={presentation}
      dataTestId={dataTestId}
      activeStageId={activeStageId}
      activeStage={activeStage}
      stagesTestId={stagesTestId}
      bodyTestId={bodyTestId}
      stageContentRef={stageContentRef}
      dialogTriggerRef={dialogTriggerRef}
      tabActivatedStageRef={tabActivatedStageRef}
      stageScrollRequestRef={stageScrollRequestRef}
      stageFocusRequestedRef={stageFocusRequestedRef}
      openDialogId={openDialogId}
      openDialogSnapshot={openDialogSnapshotRef.current}
      setOpenDialog={setOpenDialog}
      restoreDialogTrigger={restoreDialogTrigger}
      onActivate={onActivate}
      inhibited={inhibited}
      inhibitionReason={inhibitionReason}
    />
  );
};

/* The shell contract is checked INSIDE the route's failure boundary: a check
   that throws above the boundary unmounts the whole route to a blank page.
   Each region's section is validated inside that region's own boundary. */
const ValidatedOperationalWorkspace: React.FC<FmOperationalWorkspaceRootProps> = (props) => {
  assertFmOperationalWorkspaceShell(props.descriptor);
  return props.descriptor.routePending ? (
    <FmSpinner size="lg" spinning />
  ) : (
    <OperationalWorkspaceSurface {...props} />
  );
};

export const FmOperationalWorkspaceRoot: React.FC<FmOperationalWorkspaceRootProps> = (props) => {
  const failure = assertFmOperationalRenderFailure(props.descriptor);
  return (
    <FmErrorBoundary
      title={failure.title}
      description={failure.description}
      retryLabel={failure.retryLabel}
      resetKey={failure.resetKey}
      placement="route"
      data-testid={failure.testId}
    >
      <ValidatedOperationalWorkspace {...props} />
    </FmErrorBoundary>
  );
};

OperationalWorkspaceSurface.displayName = 'OperationalWorkspaceSurface';
ValidatedOperationalWorkspace.displayName = 'ValidatedOperationalWorkspace';
FmOperationalWorkspaceRoot.displayName = 'FmOperationalWorkspaceRoot';
export default FmOperationalWorkspaceRoot;
